import { usePostHog } from "shared-ui/composables/use-posthog";
import {
  type RouteDiagnosticDetails,
  routeDiagnosticDetails,
} from "shared-ui/utils/route-diagnostic-details";

interface ComponentDetails {
  name?: string | undefined;
  file?: string | undefined;
}

function componentDetails(instance: unknown): ComponentDetails | undefined {
  if (typeof instance !== "object" || instance === null) return undefined;

  try {
    const vueInstance = instance as {
      $?: { type?: Record<string, unknown> };
      $options?: Record<string, unknown>;
    };
    const type = vueInstance.$?.type;
    const options = vueInstance.$options;
    const name = [type?.["name"], type?.["__name"], options?.["name"]].find(
      (value): value is string => typeof value === "string",
    );
    const file = [type?.["__file"], options?.["__file"]].find(
      (value): value is string => typeof value === "string",
    );

    return name || file ? { name, file } : undefined;
  } catch {
    // Vue exposes a proxy here. It can throw while the component is unmounting.
    return undefined;
  }
}

export function vueErrorHandler(
  err: unknown,
  instance: unknown,
  info: string,
  logError: (...values: unknown[]) => void = console.error,
  route?: Parameters<typeof routeDiagnosticDetails>[0],
): void {
  const component = componentDetails(instance);
  const routeDetails = route ? routeDiagnosticDetails(route) : undefined;
  logError("Vue error", err, {
    vueInfo: info,
    component,
    routeName: routeDetails?.name,
    routePath: routeDetails?.path,
  });

  // Never block error handling or retain the Vue component proxy.
  queueMicrotask(() => {
    void captureWithPosthog(err, component, info, routeDetails);
  });
}

async function captureWithPosthog(
  err: unknown,
  component: ComponentDetails | undefined,
  info: string,
  route: RouteDiagnosticDetails | undefined,
): Promise<void> {
  try {
    // Lazy-load your PostHog init/composable to keep it out of entry chunk.
    const { posthog } = await usePostHog("on_reject");

    posthog.captureException(err, {
      vue_info: info,
      vue_component_name: component?.name,
      vue_component_file: component?.file,
      route_name: route?.name,
      route_path: route?.path,
    });
  } catch {
    // Error reporting must never trigger another application error.
  }
}
